import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Button, Group, Center, Loader, Text } from '@mantine/core';
import { IconPrinter } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { formatSom } from '../lib/format';
import { monthLabel, monthsListLabel } from '../lib/month';
import { amountInWords } from '../lib/numberWords';
import { SILENT_ERROR } from '../lib/queryMeta';
import './receipt.css';

export interface ReceiptData {
  id: string;
  receiptNo: string;
  amount: number;
  paidAt: string;
  forMonth?: string | null;
  method: string;
  note?: string | null;
  /** Bir necha oylik (oldindan) to'lov — har oy va ulushi. Oddiy to'lovda null. */
  months?: { forMonth: string | null; amount: number; reversed: boolean }[] | null;
  student: { id: string; fullName: string; className?: string | null };
  schoolName?: string | null;
  issuedBy?: string | null;
  issuedAt: string;
}

const METHOD_KEY: Record<string, string> = {
  CASH: 'methodCash',
  CARD: 'methodCard',
  TRANSFER: 'methodTransfer',
  OTHER: 'methodOther',
};

/** Sana-vaqt HAR DOIM Toshkent bo'yicha — chek maktabning mahalliy vaqtini ko'rsatishi shart. */
function tashkentDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', {
    timeZone: 'Asia/Tashkent',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Chop etish uchun portal — chek markupini `document.body` ning bevosita bolasi qilib
 * joylaydi. `receipt.css` dagi `@media print` qoidasi body'ning qolgan bolalarini
 * (panel, modal, bildirishnomalar) yashiradi va qog'ozga faqat shu tugun tushadi.
 */
function PrintPortal({ children }: { children: React.ReactNode }) {
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const el = document.createElement('div');
    el.id = 'receipt-print-root';
    document.body.appendChild(el);
    setHost(el);
    return () => {
      el.remove();
      setHost(null);
    };
  }, []);

  return host ? createPortal(children, host) : null;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="receipt__row">
      <span className="receipt__label">{label}</span>
      <span className="receipt__value">{value}</span>
    </div>
  );
}

/** Chekning o'zi — ekranda ham, chop etishda ham AYNI markup. */
export function ReceiptBody({ data }: { data: ReceiptData }) {
  const { t, i18n } = useTranslation();
  const methodKey = METHOD_KEY[data.method];

  return (
    <div className="receipt">
      <div className="receipt__school">{data.schoolName || t('receipt.school')}</div>
      <div className="receipt__title">{t('receipt.title')}</div>
      <div className="receipt__no">{t('receipt.no', { no: data.receiptNo })}</div>

      <hr className="receipt__rule" />

      <Row label={t('receipt.date')} value={tashkentDateTime(data.paidAt)} />
      <Row label={t('receipt.student')} value={data.student.fullName} />
      {data.student.className ? <Row label={t('receipt.class')} value={data.student.className} /> : null}
      {(() => {
        /* "Qaysi oy uchun": bitta oy — "Sentabr 2026", bir nechta — "Oktabr, Noyabr,
           Dekabr 2026" (yil oxirida bir marta). Oylar bo'yicha bo'linma faqat ulushlar
           teng bo'lmasa yoki biror oy bekor qilingan bo'lsa ko'rsatiladi — aks holda
           u bir xil summalarning ortiqcha takrori. */
        const names = t('monthsFull', { returnObjects: true }) as string[];
        const ms = data.months && data.months.length > 1 ? data.months : null;
        const label = ms ? monthsListLabel(ms.map((m) => m.forMonth), names) : monthLabel(data.forMonth, names);
        if (!label) return null;
        const detail = ms && (ms.some((m) => m.reversed) || new Set(ms.map((m) => m.amount)).size > 1);
        return (
          <>
            <Row label={t('receipt.forMonth')} value={label} />
            {detail
              ? ms!.map((m) => (
                  <Row
                    key={m.forMonth ?? ''}
                    label={`  ${monthLabel(m.forMonth, names)}`}
                    value={`${formatSom(m.amount)}${m.reversed ? ` · ${t('paymentsPage.badgeReversed')}` : ''}`}
                  />
                ))
              : null}
          </>
        );
      })()}
      <Row label={t('receipt.method')} value={methodKey ? t(`paymentsPage.${methodKey}`) : data.method} />

      <hr className="receipt__rule" />

      <div className="receipt__total">{formatSom(data.amount)}</div>
      <div className="receipt__words">
        {amountInWords(data.amount, i18n.language)} {t('currency.som')}
      </div>

      <hr className="receipt__rule" />

      {data.note ? (
        <div className="receipt__note">
          <span className="receipt__muted">{t('receipt.note')}: </span>
          {data.note}
        </div>
      ) : null}

      <div className="receipt__sign">
        <span>
          {t('receipt.issuedBy')}: <b>{data.issuedBy || '—'}</b>
        </span>
        <span className="receipt__signLine" />
      </div>

      <div className="receipt__footer">
        <div>{t('receipt.thanks')}</div>
        <div className="receipt__muted">{t('receipt.printedAt', { at: tashkentDateTime(data.issuedAt) })}</div>
      </div>
    </div>
  );
}

/**
 * Chek modali — `paymentId` berilganda ochiladi, ma'lumotni `/payments/:id/receipt`
 * dan oladi. To'lov qabul qilingandan keyin ham, ro'yxatdagi eski to'lov uchun ham
 * ayni shu modal ishlatiladi (qayta chop etish).
 */
export function PaymentReceiptModal({ paymentId, onClose }: { paymentId: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['payment-receipt', paymentId],
    queryFn: async () => (await api.get<ReceiptData>(`/payments/${paymentId}/receipt`)).data,
    enabled: !!paymentId,
    meta: SILENT_ERROR, // xato modal ichida ochiq ko'rsatiladi
  });

  return (
    <Modal opened={!!paymentId} onClose={onClose} title={t('receipt.title')} centered size="sm">
      {isLoading ? (
        <Center h={200}>
          <Loader />
        </Center>
      ) : isError || !data ? (
        <Text c="dimmed" ta="center" py="xl">
          {t('receipt.notFound')}
        </Text>
      ) : (
        <>
          <div className="receipt-preview">
            <ReceiptBody data={data} />
          </div>
          <PrintPortal>
            <ReceiptBody data={data} />
          </PrintPortal>
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>
              {t('receipt.close')}
            </Button>
            <Button leftSection={<IconPrinter size={18} />} onClick={() => window.print()}>
              {t('receipt.print')}
            </Button>
          </Group>
        </>
      )}
    </Modal>
  );
}
